import React, { memo, useEffect, useState, useRef } from 'react'
import {
  Card, Box, Typography, CardContent, Grid, Accordion, AccordionSummary, AccordionDetails, Stack, Button,
  IconButton, TextField, Checkbox, FormControlLabel
} from '@mui/material'
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'
import stylesheet from '../../patients/detail/detail-container.stylesheet'
import { RootState, useAppThunkDispatch } from '../../../redux/store'
import FilterProgramChunkComponent from './filter-program-chunk.component'
import {
  setPatientsTable, resetSelectedStage, setProgramFilter, setClinicVisitsTable
} from '../../../redux/common-data-slice'
import { PatientService } from '../../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../../utils/helper'
import FilterChunkComponent from './filter-chunk.component'
import { useLocation } from 'react-router-dom';

interface IProps {
  filterList: any,
}

const FilterComponent: React.FC<IProps> = ({ filterList }) => {
  const url = useLocation()
  const isClinicVisit = url.pathname === '/clinic-visits'

  const patientsTableState = useSelector((state: RootState) => state.commonData.patientsTable)
  const clinicVisitsTableState = useSelector((state: RootState) => state.commonData.clinicVisitsTable)
  const hospitalsData = useSelector((state: RootState) => state.metaData.metadata.HOSPITALS)

  const currentTableState = isClinicVisit ? clinicVisitsTableState : patientsTableState
  // SELECT FROM DIFFERENT STORES BASED ON ROUTE
  const patientsTable = useSelector((state: RootState) =>
    isClinicVisit ? state.commonData.clinicVisitsTable : state.commonData.patientsTable
  )

  const patientService = new PatientService()
  const classes = makeStyles(stylesheet)();
  const [filterCounts, setFilterCounts] = useState({} as any)
  const [openAccordion, setOpenAccordion] = useState(
    Helper.isAIDA()
      ? ['others']
      : ['cardiology', 'endocrinology', 'pulmonology']) as any
  const [selectedPrg, setSelectedPrg] = useState([] as any)
  const [cardiologistMeta, setCardiologistMeta] = useState([] as any)
  const [search, setSearch] = useState('' as any)
  const [activity_days, setActivityDays] = useState('' as any)
  const [clinicVisitDays, setClinicVisitDays] = useState('' as any)
  const [inactive, setInactive] = useState(false) as any
  const [approvalStatus, setApprovalStatus] = useState(false) as any
  const [clinicVisitStatus, setClinicVisitStatus] = useState('' as any)
  const [cardiologist, setCardiologist] = useState('' as any)

  const dispatch = useAppThunkDispatch()
  const inputEl = useRef(null) as any
  const inputSearch = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Reset all local filter state
    setSelectedPrg([])
    setSearch('')
    setActivityDays('')
    setClinicVisitDays('')
    setInactive(false)
    setApprovalStatus(false)
    setClinicVisitStatus('')
    setCardiologist('')
    setFilterCounts({})

    // Load clinic visit metadata if needed
    if (url.pathname === '/clinic-visits' && Helper.isAIDA()) {
      clearAllFilters()

      patientService.getClinicVisitMetaData().then((res: any) => {
        if (res?.data?.CARDIOLOGISTS)
          setCardiologistMeta(res?.data?.CARDIOLOGISTS)
      }).catch((error: any) => console.log(error))
    }
  }, [url.pathname])

  // Sync local state from Redux ONLY for the current route's table state
  useEffect(() => {
    // Only update local state from Redux if we have valid data for this route
    if (currentTableState?.selectedPrg) {
      setSelectedPrg(currentTableState.selectedPrg)
    }

    getFilteredCounts()

    // Only sync these values if they exist in the current table state
    setSearch(currentTableState.search || '')
    setActivityDays(currentTableState.activity_days || '')
    setClinicVisitDays(currentTableState?.clinic_visit_days || '')
    setInactive(
      currentTableState.inactive === 'false' ? false : currentTableState.inactive || false
    )
    setApprovalStatus(
      currentTableState.approval_status === 'false' ? false : currentTableState.approval_status || false
    )
    setClinicVisitStatus(currentTableState?.clinic_visit_status || '')
    setCardiologist(currentTableState?.cardiologist || '')
  }, [currentTableState])

  const setPrgCheckList = (data: any) => data?.map((d: any) => {
    const isCurrPrgChecked = [...selectedPrg]?.find((item: any) => item?.index === d?.index)
    return { ...d, value: d?.name, display_name: d?.display_name, checked: isCurrPrgChecked ? true : false }
  })

  const resetPrograms = () => {
    setSelectedPrg([])
    setOpenAccordion(['cardiology', 'endocrinology', 'pulmonology'])
    updateFilterData(filterCounts)
  }

  const optionChanged = (name: string, display_name: any, parent: any, subParent: any, prgId: any, targetChecked: boolean) => {
    let currParent = filterCounts['programs']?.filter((e: any) => e.display_name === parent)[0]
    let currParentIndex = filterCounts['programs']?.findIndex((e: any) => e.display_name === parent)

    if (currParentIndex !== -1) {
      let updatedItems = [...currParent['items']]?.map((m: any) => {
        if (m.display_name === subParent) {
          let currSubItem = m?.items.map((i: any) => {
            if (i?.name === name) {
              return { ...i, checked: targetChecked }
            } else {
              return { ...i }
            }
          })
          return { ...m, items: currSubItem }
        }
        else
          return { ...m }
      })

      let allSelectedPrg
      let selectedPrgList = [{}]

      if (targetChecked === true) {
        selectedPrgList = [...selectedPrg, {
          parent: parent,
          subParent: subParent,
          program: name,
          display_name: display_name,
          index: prgId
        }]
        setSelectedPrg(selectedPrgList)
        allSelectedPrg = (selectedPrg?.length > 0)
          ? [...selectedPrg].map((p: any) => p.program).join(',') + ',' + name
          : name
      }
      else {
        selectedPrgList = [...selectedPrg]?.filter((item: any) => item?.index !== prgId)
        setSelectedPrg(selectedPrgList)
        allSelectedPrg = [...selectedPrgList].map((p: any) => p.program).join(',')
      }

      let updatedFilterCounts = { ...filterCounts }
      let updatedPrgram = [...filterCounts['programs']]
      updatedPrgram[currParentIndex] = { ...currParent, 'items': updatedItems }
      updatedFilterCounts['programs'] = updatedPrgram

      dispatch(isClinicVisit ? setClinicVisitsTable(updatedFilterCounts) : setProgramFilter(updatedFilterCounts))
      dispatch(
        isClinicVisit
          ? setClinicVisitsTable({
            ...patientsTable,
            isSubmit: 'reset',
            page: 1,
            limit: 50,
            programs: [],
            selectedPrg: '',
          })
          : setPatientsTable({
            ...patientsTable,
            isSubmit: 'reset',
            page: 1,
            limit: 50,
            programs: allSelectedPrg,
            selectedPrg: selectedPrgList,
          })
      )
    }
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()

    dispatch(
      isClinicVisit
        ? setClinicVisitsTable({
          ...patientsTable,
          page: 1,
          search: search,
          inactive: inactive,
          approval_status: approvalStatus,
          clinic_visit_status: clinicVisitStatus,
          cardiologist: cardiologist || '',
          clinic_visit_days: clinicVisitDays !== '' ? clinicVisitDays : '',
          activity_days: activity_days > 0 ? activity_days : '',
          sort: '',
          isSubmit: 'submit',
          shouldRestoreState: 'false',
          preserveData: '',
          scrollPosition: 0,
        })
        : setPatientsTable({
          ...patientsTable,
          page: 1,
          search: search,
          inactive: inactive,
          approval_status: approvalStatus,
          clinic_visit_status: clinicVisitStatus,
          cardiologist: cardiologist || '',
          clinic_visit_days: clinicVisitDays !== '' ? clinicVisitDays : '',
          activity_days: activity_days > 0 ? activity_days : '',
          sort: '',
          isSubmit: 'submit',
          shouldRestoreState: 'false',
          preserveData: '',
          scrollPosition: 0,
        })
    )
  }

  const resetClinicVisitData = () => {
    setClinicVisitsTable({
      limit: 50,
      page: 1,
      sort: '',
      search: '',
      programs: '',
      hospitals: '',
      status: '',
      gender: '',
      stage: '',
      state: '',
      priority: '',
      inactive: 'false',
      activity_days: '',
      clinic_visit_days: '',
      approval_status: 'false',
      clinic_visit_status: '',
      cardiologist: '',
      isSubmit: 'submit',
      shouldRestoreState: 'false',
      preserveData: '',
      scrollPosition: 0,
    })
  }

  const clearAllFilters = () => {
    // Only reset selected stage if not clinic visit
    if (!isClinicVisit) {
      dispatch(resetSelectedStage())
    }

    isClinicVisit
      ? resetClinicVisitData()
      : dispatch(setPatientsTable({
        limit: 50,
        page: 1,
        sort: '',
        search: '',
        programs: '',
        hospitals: '',
        status: '',
        gender: '',
        stage: '',
        state: '',
        priority: '',
        inactive: 'false',
        activity_days: '',
        clinic_visit_days: '',
        approval_status: 'false',
        clinic_visit_status: '',
        cardiologist: '',
        isSubmit: 'submit',
        shouldRestoreState: 'false',
        preserveData: '',
        scrollPosition: 0,
      })
    )
  }

  const updateFilterData = (currData: any) => {
    const updatedData = currData?.programs

    if (selectedPrg?.length > 0) {
      const updatedPrograms = updatedData?.map((item: any) => {
        let currParent = selectedPrg?.filter((e: any) => e.parent === item?.display_name)[0]
        let currParentIndex = selectedPrg?.findIndex((e: any) => e.parent === item?.display_name)

        if (currParent && currParentIndex !== -1) {
          let updatedItems = item['items']?.map((m: any) => {
            let currSubParent = selectedPrg?.filter((e: any) => e.parent === item?.display_name
              && e.subParent === m.display_name)

            if (currSubParent?.length > 0) {
              let currSubItem = m?.items.map((i: any) => {
                let currProgram = selectedPrg?.filter((e: any) => e.index === i?.index)[0]
                return (i?.name === currProgram?.program)
                  ? { ...i, checked: true }
                  : { ...i, checked: false }
              })
              return { ...m, items: currSubItem }
            }
            else
              return { ...m }
          })
          return { ...item, items: updatedItems }
        } else {
          return { ...item }
        }
      })
      const sortedPrg = updatedPrograms?.length > 2
        ? updatedPrograms?.sort((a: any, b: any) => a.display_name > b.display_name ? 1 : -1)
        : updatedPrograms

      let updatedFilterCounts = { ...currData }
      updatedFilterCounts['programs'] = sortedPrg
      setFilterCounts(updatedFilterCounts)
    } else
      setFilterCounts(currData)
  }

  const getFilteredCounts = () => {
    if (Object.keys(filterCounts)?.length <= 0) {
      patientService.getFilterCounts(
        Helper.isAIDA(),
        currentTableState?.programs || '',
        currentTableState?.hospitals || '',
        currentTableState.inactive.toString() === 'true' ? 'inactive' : 'active'
      ).then((res: any) => {
        if (res?.data)
          updateFilterData(res.data)
      }).catch((error: any) => setError(error))
    } else
      updateFilterData(filterCounts)
  }

  const isFilters = () => Object.values(currentTableState).slice(2).join('')?.replace(/submit|reset/g, '')

  const setError = (error: any) => {
    toast.error(Helper.getErrorText(error), {
      position: 'bottom-center',
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
    })
  }

  const handleAccordion = (key: any) => {
    setOpenAccordion(
      openAccordion.indexOf(key) !== -1
        ? openAccordion.filter((item: any) => item !== key)
        : [...openAccordion, key]
    )
  }

  const setDataList = (key: string) => {
    if (filterCounts && filterCounts[key] !== undefined) {
      const res = Object.keys(filterCounts[key])?.map((item: any) => {
        let isChecked = false
        if (key === 'hospitals') {
          const hospitalList = localStorage.getItem("atman-hospitals")
          isChecked = hospitalList?.includes(item) || false
        }
        return { name: item, value: item, count: filterCounts[key][item] || 0, checked: isChecked }
      }) || []
      if (key === 'status') {
        let statusObj = [] as any
        return statusObj = [
          ...res.filter((i: any) => i.name === 'RECOMMENDED'),
          ...res.filter((i: any) => i.name === 'ORDERED'),
          ...res.filter((i: any) => i.name === 'RESULTS_AVAILABLE'),
          ...res.filter((i: any) => i.name === 'DEFERRED'),
          ...res.filter((i: any) => i.name === 'NOT_AVAILABLE')
        ]
      }
      else if (key === 'hospitals') {
        return res?.sort((a: any, b: any) => {
          const labelA = (hospitalsData && hospitalsData[a.name]) || a.name
          const labelB = (hospitalsData && hospitalsData[b.name]) || b.name
          return labelA.localeCompare(labelB)
        })
      }
      else return res?.sort((a: any, b: any) => a.name > b.name ? 1 : -1)
    }
  }

  const getProgramTitleCount = (title: any, count = 0) => {
    return <Grid container direction="row" justifyContent="space-between" alignItems="baseline" style={{ flexWrap: "nowrap" }}>
      <Grid item>
        <Typography className={classes.desc} variant="body2">
          {Helper.toCamelCase(title)}
        </Typography>
      </Grid>
      <Grid item>
        <Typography className={classes.desc} variant="body2" color="textSecondary"><small>{(count)}</small></Typography>
      </Grid>
    </Grid>
  }

  const getSubProgramList = (item: any, parent: any) => {
    return item !== undefined && item?.length > 0 &&
      [...item]?.sort((a: any, b: any) => a.display_name > b.display_name ? 1 : -1)
        ?.map((d: any, index: any) => (
          <Accordion
            square
            key={`prg-${d?.display_name}-${index}`}
            expanded={openAccordion.includes(d?.display_name) ? true : false}
            className={classes.programCategory}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className={classes.infoIconFont} />}
              ref={inputEl}
              onClick={() => handleAccordion(d?.display_name)}
              sx={{ padding: '0px !important' }}
            >
              {getProgramTitleCount(d?.display_name, d?.count)}
            </AccordionSummary>
            <AccordionDetails sx={{
              '&.MuiAccordionDetails-root.MuiAccordionDetails-root': {
                paddingRight: '2px !important'
              }
            }}>
              <Box className={classes.fullWidth}>
                <FilterProgramChunkComponent
                  input={setPrgCheckList(d?.items)}
                  optionChanged={(name: any, display_name: any, index: any, targetChecked: any) =>
                    optionChanged(name, display_name, parent, d?.display_name, index, targetChecked)}
                  filterCount={d?.count}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
  }

  const getProgram = () => {
    if ((Object.keys(filterCounts)?.length) < 2 && filterCounts?.programs === undefined) return
    let prgList = filterCounts['programs']
    prgList = [...prgList]?.sort((a: any, b: any) => a.display_name > b.display_name ? 1 : -1)

    return <Grid container item xs={12} direction="row">
      <Grid item className={classes.fullWidth}>
        {Object.keys(prgList)?.length > 0 && Object.keys(prgList)?.map((key: any, index: any) => (
          <Accordion
            key={`${key}-${index}`}
            square
            expanded={openAccordion.includes(prgList[key]?.display_name) ? true : false}
          >
            <AccordionSummary
              ref={inputEl}
              onClick={() => handleAccordion(prgList[key]?.display_name)}
              expandIcon={<ExpandMoreIcon className={classes.infoIconFont} />}
              sx={{ padding: '0px !important' }}
            >
              {getProgramTitleCount(prgList[key]?.display_name, prgList[key]?.count)}
            </AccordionSummary>
            <AccordionDetails sx={{
              '&.MuiAccordionDetails-root.MuiAccordionDetails-root': {
                padding: '0rem 0rem 0.1rem 0rem !important',
              }
            }}>
              {getSubProgramList(prgList[key]?.items, prgList[key]?.display_name)}
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Grid>
  }

  const getInactiveStatus = () => {
    return <Box pb={0.8} pt={0.5}
      className={`${classes.filterChunkContainer} ${!Helper.isAIDA() ? classes.filterBorderBottom : ''}`}
    >
      <Typography className={classes.desc} variant="body2"><strong>{`Status`}</strong></Typography>
      <Stack direction={'row'} spacing={0} alignItems={'center'} justifyContent={'flex-start'}
        className={classes.filterChunkItems} pl={1}>
        <FormControlLabel
          control={
            <Checkbox
              name="patient_inactive"
              size="small"
              className={classes.zeroPadding}
              sx={{ transform: "scale(0.6) !important" }}
              checked={Boolean(inactive)}
              onChange={(e: any, checked: boolean) => setInactive(checked)}
            />
          }
          label={
            <Typography className={classes.desc} variant="body2">
              {inactive === true ? <strong>{`Show inactive`}</strong> : `Show inactive`}
            </Typography>
          }
        />
      </Stack>
    </Box>
  }

  const getApprovalStatus = () => {
    return <Box pb={0.8} pt={0.5} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
      <Typography className={classes.desc} variant="body2"><strong>{`Approval`}</strong></Typography>
      <Stack direction={'row'} spacing={0} alignItems={'center'} justifyContent={'flex-start'}
        className={classes.filterChunkItems} pl={1}>
        <FormControlLabel
          control={
            <Checkbox
              name="patient_approval_status"
              size="small"
              className={classes.zeroPadding}
              sx={{ transform: "scale(0.6) !important" }}
              checked={Boolean(approvalStatus)}
              onChange={(e: any, checked: boolean) => setApprovalStatus(checked)}
            />
          }
          label={
            <Typography className={classes.desc} variant="body2">
              {approvalStatus === true ? <strong>{`Pending approval`}</strong> : `Pending approval`}
            </Typography>
          }
        />
      </Stack>
    </Box>
  }

  const getClinicVisitStatus = () => {
    const visitOption = [
      { key: 'COMPLETED', value: 'Completed' },
      { key: 'PENDING', value: 'Pending' }
    ];

    return (
      <Box pb={0.8} pt={1} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
        <Typography className={classes.desc} variant="body2">
          <strong>Clinic visit status</strong>
        </Typography>
        <Stack
          direction={'column'}
          spacing={0}
          alignItems={'left'}
          justifyContent={'flex-start'}
          className={classes.filterChunkItems}
          pl={1}
        >
          {visitOption.map((option) => {
            const statusArray = clinicVisitStatus ? clinicVisitStatus.split(',') : [];
            const isChecked = statusArray.includes(option.key);
            return (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    name={`clinic_visit_status_${option.key}`}
                    size="small"
                    className={classes.zeroPadding}
                    sx={{ transform: "scale(0.6) !important" }}
                    checked={isChecked}
                    onChange={(e, checked) => {
                      if (checked) {
                        if (clinicVisitStatus && clinicVisitStatus.length > 0 &&
                          !clinicVisitStatus.includes(option.key)) {
                          setClinicVisitStatus(`${clinicVisitStatus},${option.key}`);
                        } else if (!clinicVisitStatus || clinicVisitStatus.length === 0) {
                          setClinicVisitStatus(option.key);
                        }
                      } else {
                        const statusArray = clinicVisitStatus.split(',').filter((status: string) => status !== option.key);
                        setClinicVisitStatus(statusArray.join(','));
                      }
                    }}
                  />
                }
                label={
                  <Typography className={classes.desc} variant="body2">
                    {isChecked ? <strong>{option.value}</strong> : option.value}
                  </Typography>
                }
              />
            );
          })}
        </Stack>
      </Box>
    );
  };

  const getCardiologist = () => {
    return (
      <Box pb={0.8} pt={1} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
        <Typography className={classes.desc} variant="body2">
          <strong>Cardiologist</strong>
        </Typography>
        <Stack direction="column" alignItems="flex-start" className={classes.filterChunkItems} pl={1}>
          {cardiologistMeta?.map((option: any) => {
            return (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    name={`cardiologist_${option.id}`}
                    size="small"
                    className={classes.zeroPadding}
                    sx={{ transform: "scale(0.6) !important" }}
                    checked={cardiologist.includes(option.id.toString())}
                    onChange={(e, checked) => {
                      if (checked) {
                        if (!cardiologist?.includes(option.id.toString())) {
                          const newValue = cardiologist ? `${cardiologist},${option.id}` : option.id.toString();
                          setCardiologist(newValue);
                        }
                      } else {
                        const statusArray = cardiologist.split(',').filter((id: any) => id !== option.id.toString());
                        setCardiologist(statusArray.join(','));
                      }
                    }}
                  />
                }
                label={
                  <Typography className={classes.desc} variant="body2">
                    {cardiologist.includes(option.id.toString()) ?
                      <strong>{option.name}</strong> :
                      option.name
                    }
                  </Typography>
                }
              />
            );
          })}
        </Stack>
      </Box>
    );
  };

  const getClinicVisitOverDueStatus = () => {
    const visitOption = [{ key: 'OVERDUE', value: 'Show overdue' }];

    return (
      <Box pb={0.8} pt={0.5} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
        <Typography className={classes.desc} variant="body2"><strong>Overdue</strong></Typography>
        <Stack
          direction={'column'}
          spacing={0}
          alignItems={'left'}
          justifyContent={'flex-start'}
          className={classes.filterChunkItems}
          pl={1}
        >
          {visitOption.map((option) => {
            const statusArray = clinicVisitStatus ? clinicVisitStatus.split(',') : [];
            const isChecked = statusArray.includes(option.key);
            return (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    name={`clinic_visit_status_${option.key}`}
                    size="small"
                    className={classes.zeroPadding}
                    sx={{ transform: "scale(0.6) !important" }}
                    checked={isChecked}
                    onChange={(e, checked) => {
                      if (checked) {
                        if (clinicVisitStatus && clinicVisitStatus.length > 0 &&
                          !clinicVisitStatus.includes(option.key)) {
                          setClinicVisitStatus(`${clinicVisitStatus},${option.key}`);
                        } else if (!clinicVisitStatus || clinicVisitStatus.length === 0) {
                          setClinicVisitStatus(option.key);
                        }
                      } else {
                        const statusArray = clinicVisitStatus.split(',').filter((status: string) => status !== option.key);
                        setClinicVisitStatus(statusArray.join(','));
                      }
                    }}
                  />
                }
                label={
                  <Typography className={classes.desc} variant="body2">
                    {isChecked ? <strong>{option.value}</strong> : option.value}
                  </Typography>
                }
              />
            );
          })}
        </Stack>
      </Box>
    );
  };

  const getActivityFilter = () => {
    return <Box pb={0.8} pt={0.5} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
      <Typography className={classes.desc} variant="body2"><strong>{`Activity`}</strong></Typography>
      <Stack direction={'row'} spacing={0.3} pl={0.2} alignItems={'baseline'} justifyContent={'flex-start'} flexWrap={"wrap"}>
        <Typography className={classes.desc} variant="body2">
          Patients with no activity since
        </Typography>
        <Box className={classes.measurementInputOuter}>
          <TextField
            id={'activity_days'}
            variant="standard"
            type="number"
            inputProps={{
              min: 1,
              sx: { width: '1.8rem', textAlign: 'right', fontSize: '0.7rem', padding: '0.1rem' }
            }}
            value={activity_days}
            onChange={(e) => setActivityDays(e.target.value)}
          />
        </Box>
        <Typography className={classes.desc} variant="body2">
          days
        </Typography>
      </Stack>
    </Box>
  }

  const getVisitDateFilter = () => {
    return (
      <Box pb={0.8} pt={0.5} className={`${classes.filterChunkContainer} ${classes.filterBorderBottom}`}>
        <Typography className={classes.desc} variant="body2"><strong>Visit date</strong></Typography>
        <Stack direction={'row'} alignItems={'center'} className={classes.filterChunkItems} pl={1}>
          <FormControlLabel
            control={
              <Checkbox
                name="clinic_visit_days"
                size="small"
                className={classes.zeroPadding}
                sx={{ transform: "scale(0.6) !important" }}
                checked={clinicVisitDays.includes('today')}
                onChange={(e, checked) => {
                  const numericPart = clinicVisitDays.replace('today', '').replace(/,+/g, '').trim();
                  checked
                    ? setClinicVisitDays(numericPart ? `today, ${numericPart}` : 'today')
                    : setClinicVisitDays(clinicVisitDays.replace('today', '').replace(/,+/g, '').trim())
                }}
              />
            }
            label={
              <Typography className={classes.desc} variant="body2">
                {clinicVisitDays.includes('today') ? <strong>Today's visits</strong> : "Today's visits"}
              </Typography>
            }
          />
        </Stack>
        <Stack direction={'row'} spacing={0.3} pl={0.5} alignItems={'baseline'} justifyContent={'flex-start'} flexWrap={"wrap"}>
          <Typography className={classes.desc} variant="body2">Clinic date</Typography>
          <Box className={classes.measurementInputOuter}>
            <TextField
              id={'clinic_visit_days'}
              variant="standard"
              type="number"
              inputProps={{
                min: 1,
                sx: { width: '1.8rem', textAlign: 'right', fontSize: '0.7rem', padding: '0.1rem' }
              }}
              size="small"
              value={clinicVisitDays.replace('today', '').replace(/,+/g, '').trim()}
              onChange={(e) => {
                const newValue = e.target.value;
                (clinicVisitDays.includes('today'))
                  ? setClinicVisitDays(newValue ? `today,${newValue}` : 'today')
                  : setClinicVisitDays(newValue);
              }}
            />
          </Box>
          <Typography className={classes.desc} variant="body2">days ahead</Typography>
        </Stack>
      </Box>
    );
  }

  const getProgramFilter = () => {
    return <Box pt={0.2} pb={0.5}>
      <Accordion key={`prg`} defaultExpanded={true} square>
        <AccordionSummary
          sx={{ padding: '0px !important', marginLeft: '-2px' }}
          ref={inputEl}
          expandIcon={<ExpandMoreIcon className={classes.infoIconFont} />}
        >
          <Typography className={classes.desc} variant="body2"><strong>Program</strong></Typography>
        </AccordionSummary>
        <AccordionDetails sx={{
          marginLeft: '-2px !important',
          '&.MuiAccordionDetails-root.MuiAccordionDetails-root': {
            paddingTop: '2px !important',
            paddingBottom: '2px !important',
            paddingRight: '0px !important'
          }
        }}>
          {getProgram()}
        </AccordionDetails>
      </Accordion>
    </Box>
  }

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'var(--color-background-subtle)' }}>
      <CardContent sx={{ padding: '0.5rem 0.3rem 0.5rem 0.8rem !important' }}>
        <Grid container className={classes.filterBorderBottom} lg={12} spacing={0.2} direction="column" justifyContent="space-between" alignItems={'baseline'}>
          <Grid item>
            <Typography pt={0.3} className={classes.subHeader} variant="subtitle1"><strong>Filters</strong></Typography>
          </Grid>
          <Grid item>
            <TextField
              size="small"
              id="search"
              variant="outlined"
              value={search}
              ref={inputSearch}
              className={classes.searchFieldBg}
              sx={{
                padding: "2px",
                width: '12.5rem',
                '& .MuiInputBase-root ': {
                  height: '1.4rem !important'
                }
              }}
              onChange={(e: any) => setSearch(e.target.value)}
              onKeyUp={(e: any) => {
                e.preventDefault();
                if (e.key === 'Enter')
                  handleSubmit(e)
              }}
              placeholder="search"
              InputProps={{
                endAdornment: (
                  <>
                    <IconButton
                      className={classes.sizeSmall}
                      onClick={() => setSearch('')}
                    >
                      {currentTableState.search ? (
                        <CloseIcon className={classes.schInfoIcon} />
                      ) : (
                        <SearchIcon className={classes.schInfoIcon} />
                      )}
                    </IconButton>
                  </>
                ),
                inputProps: { style: { padding: '0 0 0 5px', fontSize: '0.7rem' } },
                classes: { adornedEnd: classes.adornedEnd }
              }}
            />
          </Grid>
          <Grid item>
            <Stack direction={'row'} spacing={1.2} justifyContent={'flex-start'} pt={0.5} pb={1} pl={0.5}>
              <Button variant="outlined"
                className={classes.filterActiveBth}
                onClick={(e: any) => handleSubmit(e)}
              >
                <Typography className={classes.desc} variant="body2"><small>{'Apply'}</small></Typography>
              </Button>
              <Button variant="outlined"
                className={isFilters() ? classes.filterActiveBth : classes.filterBtn}
                onClick={() => isFilters() ? clearAllFilters() : null}
              >
                <Typography className={classes.desc} variant="body2"><small>{'Clear all'}</small></Typography>
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {!Helper.isAIDA()
          ? <>
            <FilterChunkComponent
              name="Hospitals"
              stateKey='hospitals'
              input={setDataList('hospitals')}
              filterCount={filterCounts?.hospitals}
            />
            {getInactiveStatus()}
            {getActivityFilter()}
            {getProgramFilter()}
            <FilterChunkComponent
              name="Stage"
              stateKey='stage'
              input={setDataList('stage')}
              filterCount={filterCounts?.stage}
            />
          </>
          : <>
            {url.pathname === '/clinic-visits'
              ? <>
                {getCardiologist()}
                {getClinicVisitStatus()}
                {getClinicVisitOverDueStatus()}
                {getVisitDateFilter()}
              </>
              : <>
                <FilterChunkComponent
                  name="Stage"
                  stateKey='stage'
                  input={setDataList('stage')}
                  filterCount={filterCounts?.stage}
                />
                <FilterChunkComponent
                  name="Test status"
                  stateKey='status'
                  input={setDataList('status')}
                  filterCount={filterCounts?.status}
                />
                {getApprovalStatus()}
                {getInactiveStatus()}
              </>
            }
          </>
        }
      </CardContent>
    </Card>
  )
}

export default memo(FilterComponent);